import {
    EarlyPaidAdoption,
    EarlyPaidAdoptionResponse
} from '@lsq-frontend/remote-services/analytics-graphql/src/lib/utils/types';
import chartUtils, { dateSort, earlyPaidColors, mapCentsToDollars } from './chart-utils';
import { ChartFilters, EarlyPaidAdoptionChart } from './chart-types';
import { format } from 'date-fns';
import { DateFormat } from '@lsq-frontend/common-ui';

const getEarlyPaidAdoptionConfig = (data: EarlyPaidAdoptionChart) => {
    const chartConfig = {
        chart: {
            height: '500px'
        },
        title: {
            text: null
        },
        legend: { enabled: false },
        xAxis: [
            {
                categories: data.categories,
                title: {
                    text: 'Month of History Date',
                    style: chartUtils.getTextStyle()
                },
                labels: {
                    style: chartUtils.getTextStyle()
                }
            }
        ],
        yAxis: [
            {
                min: 0,
                title: {
                    text: 'Avg. AP Amount ($)',
                    style: chartUtils.getTextStyle()
                },
                labels: {
                    style: chartUtils.getTextStyle()
                },
                plotLines: [
                    {
                        width: 2,
                        value: mapCentsToDollars(data.creditLimit),
                        zIndex: 5,
                        label: {
                            text: `Buyer Credit Limit: ${chartUtils.abbreviatorFormatter(
                                mapCentsToDollars(data.creditLimit)
                            )}`,
                            style: {
                                ...chartUtils.getTextStyle(14),
                                fontWeight: 'bold',
                                color: 'white',
                                paintOrder: 'stroke',
                                stroke: 'black',
                                strokeWidth: '2px',
                                strokeLinecap: 'butt',
                                strokeLinejoin: 'miter'
                            }
                        }
                    }
                ]
            },
            {
                min: 0,
                title: {
                    text: null,
                    style: chartUtils.getTextStyle()
                },
                labels: {
                    style: chartUtils.getTextStyle()
                },
                opposite: true
            }
        ],
        tooltip: {
            style: chartUtils.getTextStyle(),
            formatter: function (this: any) {
                const header = `Month of History Date: <b>${this.x}</b><br/>`;
                const balance = this.points[0].y || 0;
                const earlyPaid = this.points[1].y || 0;
                const body =
                    'Avg. AP Balance: <b>' +
                    chartUtils.abbreviatorFormatter(balance) +
                    '</b></br/>' +
                    'Avg. Early Paid Balance: <b>' +
                    chartUtils.abbreviatorFormatter(earlyPaid) +
                    '</b>';
                return '<div>' + header + body + '</div>';
            },
            shared: true
        },
        plotOptions: {
            series: {
                fillOpacity: 1
            }
        },
        exporting: {
            enabled: false
        },
        colors: earlyPaidColors,
        series: [
            {
                name: null,
                type: 'area',
                showInLegend: false,
                data: data.openInvoiceAmount.map(mapCentsToDollars)
            },
            {
                name: '  ',
                type: 'area',
                showInLegend: false,
                data: data.fundedAmount.map(mapCentsToDollars),
                dataLabels: {
                    enabled: true,
                    style: chartUtils.getTextStyle(),
                    formatter: function (this: any) {
                        const operation = (this.y * 100) / mapCentsToDollars(data.creditLimit);
                        if (isFinite(operation)) {
                            return operation.toFixed(2) + ' %';
                        }
                    }
                }
            }
        ]
    };
    return chartConfig;
};

export default getEarlyPaidAdoptionConfig;

export const GetEarlyPaidAdoptionSchema = (
    data: EarlyPaidAdoptionResponse,
    filters: ChartFilters
) => {
    if (data.fundingUtilizations.length === 0) {
        throw new Error('Empty data');
    }
    const sourceData = data.fundingUtilizations.sort(dateSort);

    const categories = sourceData.map((utilization: EarlyPaidAdoption) =>
        format(utilization.date, DateFormat.MonthNumberDashYear)
    );

    const openInvoiceAmount = sourceData.map(
        (utilization: EarlyPaidAdoption) => utilization.openInvoiceAmount.cents
    );
    const fundedAmount = sourceData.map(
        (utilization: EarlyPaidAdoption) => utilization.fundedAmount.cents
    );

    const creditLimit = sourceData[0].medianCreditLimit.cents;
    const schema = getEarlyPaidAdoptionConfig({
        openInvoiceAmount,
        fundedAmount,
        creditLimit,
        categories: categories
    });
    return schema;
};
